/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Inline, Text, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import CloseIcon from '@atlaskit/icon/core/close';
import Anchor from '@atlaskit/primitives/anchor';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, cx, jsx } from '@atlaskit/css';

import { fg } from '@confluence/feature-gating';
import { ADMIN_NEW_FEATURES } from '@confluence/named-routes';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';

const NAVIGATION_RESOURCE_URL = 'https://support.atlassian.com/navigation/resources/';

export const i18n = defineMessages({
	bannerContentWithLink: {
		id: 'side-navigation.global-banner.content-with-link',
		defaultMessage:
			'The <a>new navigation</a> is now available and will be automatically turned on in the coming weeks. Turn it on for your team now or start with just yourself.',
		description:
			'Banner content to promote the new navigation early and prompt admin users to try before GA with resource link.',
	},
	bannerReminderContentWithLink: {
		id: 'side-navigation.global-banner-reminder.content-with-link',
		defaultMessage:
			'The <a>new navigation</a> will be automatically turned on the week of May 19. If you haven’t yet, turn it on for yourself or your team now.',
		description:
			'Banner content to promote the new navigation and prompt admin users to try before they are automatically opted in.',
	},
	bannerCTA: {
		id: 'side-navigation.global-banner.cta',
		description: 'Link to a page that allows user to sign up for live pages beta',
		defaultMessage: 'Go to settings',
	},
	bannerCloseLabel: {
		id: 'side-navigation.global-banner.close.label',
		description:
			'Accessibility label for close button on the right side of the banner that allows user to dismiss the banner',
		defaultMessage: 'Close',
	},
});

const styles = cssMap({
	banner: {
		padding: token('space.050'),
		width: '100%',
		backgroundColor: token('color.background.neutral.bold'),
	},
	bannerHeightNav3: {
		height: '56px', // same value used for navHeight in BannerStateContainer in @confluence/banners
	},
	bannerHeight: {
		height: '48px', // same value used for navHeight in BannerStateContainer in @confluence/banners
	},
	bannerContent: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.100'),
	},
});

export type Nav4PromptBannerComponentProps = {
	onDismiss: () => void;
};

export const Nav4PromptBannerComponent = ({ onDismiss }: Nav4PromptBannerComponentProps) => {
	const isNav4Enabled = useIsNav4Enabled();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'displayed',
					actionSubject: 'banner',
					actionSubjectId: 'nav4PromptBanner',
					attributes: {
						isReminderBanner: fg('confluence_nav4_admin_reminder_banner'),
					},
					source: 'nav4PromptBanner',
				},
			}).fire();
		}
	}, [createAnalyticsEvent]);

	const handleLinkClick = (e) => {
		e.preventDefault();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'nav4PromptBanner',
				source: 'nav4PromptBanner',
				attributes: {
					isReminderBanner: fg('confluence_nav4_admin_reminder_banner'),
				},
			},
		}).fire();
		window.open(ADMIN_NEW_FEATURES.toUrl(), '_blank');
	};

	const handleResourceLinkClick = (e) => {
		e.preventDefault();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'nav4PromptBanner',
				source: 'nav4PromptBanner',
				attributes: {
					isReminderBanner: fg('confluence_nav4_admin_reminder_banner'),
				},
			},
		}).fire();
		window.open(NAVIGATION_RESOURCE_URL);
	};

	return (
		<Flex
			justifyContent="space-between"
			xcss={cx(
				styles.bannerContent,
				styles.banner,
				isNav4Enabled ? styles.bannerHeight : styles.bannerHeightNav3,
			)}
			as="div"
			alignItems="center"
		>
			<Inline>
				<Text color="color.text.inverse">
					{/* TODO: update the banner copy for enterprise customers - DISCO-4023 */}
					{fg('confluence_nav4_admin_reminder_banner') ? (
						<FormattedMessage
							{...i18n.bannerReminderContentWithLink}
							values={{
								a: (chunks) => (
									<Anchor
										href={NAVIGATION_RESOURCE_URL}
										onClick={handleResourceLinkClick}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
										style={{ color: token('color.text.inverse') }}
									>
										{chunks}
									</Anchor>
								),
							}}
						/>
					) : (
						<FormattedMessage
							{...i18n.bannerContentWithLink}
							values={{
								a: (chunks) => (
									<Anchor
										href={NAVIGATION_RESOURCE_URL}
										onClick={handleResourceLinkClick}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
										style={{ color: token('color.text.inverse') }}
									>
										{chunks}
									</Anchor>
								),
							}}
						/>
					)}
				</Text>
				&nbsp;
				<Anchor
					href={ADMIN_NEW_FEATURES.toUrl()}
					onClick={handleLinkClick}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ color: token('color.text.inverse') }}
				>
					<FormattedMessage {...i18n.bannerCTA} />
				</Anchor>
			</Inline>
			<IconButton
				icon={(iconProps) => (
					<CloseIcon {...iconProps} color={token('color.icon.inverse')} spacing="none" />
				)}
				label={<FormattedMessage {...i18n.bannerCloseLabel} />}
				appearance="subtle"
				onClick={onDismiss}
			/>
		</Flex>
	);
};
